import Link from 'components/gruenland/atoms/link';
import Typography from 'components/gruenland/atoms/typography';
import { sortCategoryGroups } from 'helpers/categoryHelpers/categoryGroupSort';
import { Category } from 'types/entity/category';
import { Tile } from '../../../types';
import HeaderDropdownTile from './header-menu-tile';
import { Reference } from 'types/reference';
import { useEffect, useMemo, useState } from 'react';
import { TYPOGRAPHY_MENU_LABEL_BIG, TYPOGRAPHY_MENU_LABEL_SMALL } from 'lib/typography';
import classNames from 'classnames';
import { useFormat } from 'helpers/hooks/useFormat';

export interface Props {
  categoryKey?: string;
  show: boolean | undefined;
  categories: Category[];
  tileContent?: Tile[];
  col1Label: string;
  col1Links: { label: string; link: Reference }[];
  onClick?: () => void;
}

const MenuDropdown: React.FC<Props> = ({
  categoryKey,
  show,
  categories,
  tileContent,
  col1Label,
  col1Links,
  onClick,
}) => {
  const groups = useMemo(() => {
    return sortCategoryGroups(categories);
  }, [categories]);

  const [selectedGroup, setSelectedGroup] = useState(groups[0]);

  useEffect(() => {
    setSelectedGroup(groups[0]);
  }, [groups]);

  const { formatMessage } = useFormat({ name: 'gruenland' });

  return (
    show &&
    selectedGroup && (
      <div className="absolute inset-x-0 bottom-0 z-20 grid min-h-[300px] translate-y-full grid-cols-5 bg-white px-10 pb-10 pt-5">
        <div>
          <p className={TYPOGRAPHY_MENU_LABEL_BIG}>{col1Label}</p>
          <ul className="mt-[10px]">
            {col1Links.map((x, i) => (
              <li key={i} className={TYPOGRAPHY_MENU_LABEL_SMALL}>
                <Link link={x.link}>{x.label}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <ul className="flex flex-col gap-5">
            {groups.map((x, i) => (
              <li key={i}>
                <button
                  onClick={() => setSelectedGroup(x)}
                  className={classNames(TYPOGRAPHY_MENU_LABEL_BIG, {
                    'border-b-4 border-primary-yellow pb-1': x === selectedGroup,
                  })}
                >
                  {formatMessage({ id: `categoryGroup.${x}`, defaultMessage: x })}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div>
          {categories
            .filter((x) => x.customGroup === selectedGroup)
            .map((cat) => (
              <div key={cat.categoryId}>
                {cat.depth === 1 ? (
                  <>
                    <div className="w-min pb-2">
                      <Link link={cat?._url} variant="menu-header" className="whitespace-nowrap">
                        <Typography
                          className={classNames(
                            TYPOGRAPHY_MENU_LABEL_SMALL,
                            'border-primary-yellow hover:border-b-4 hover:leading-[32px]',
                          )}
                        >
                          {cat.name}
                        </Typography>
                      </Link>
                    </div>
                    {cat.descendants &&
                      cat.descendants.map((field) => (
                        <div key={field.categoryId} onClick={onClick} className="w-min pb-2">
                          <Link link={field?._url} variant="menu-item" className="whitespace-nowrap">
                            <Typography
                              className={classNames(
                                TYPOGRAPHY_MENU_LABEL_SMALL,
                                'border-primary-yellow hover:border-b-4 hover:leading-[32px]',
                              )}
                            >
                              {field.name}
                            </Typography>
                          </Link>
                        </div>
                      ))}
                  </>
                ) : (
                  <Link key={cat.categoryId} link={cat?._url} variant="menu-header">
                    <Typography
                      className={classNames(
                        TYPOGRAPHY_MENU_LABEL_SMALL,
                        'border-primary-yellow hover:border-b-4 hover:leading-[32px]',
                      )}
                    >
                      {cat.name}
                    </Typography>
                  </Link>
                )}
              </div>
            ))}
        </div>
        <div className="col-span-2 flex justify-end space-x-2">
          {tileContent?.map(
            (x, i) => x.tileCategoryKey === categoryKey && <HeaderDropdownTile key={i} tileContent={x} />,
          )}
        </div>
      </div>
    )
  );
};

export default MenuDropdown;
